import React, { useEffect } from "react";
import { Stack, Text } from "sprint-common-web";

import {
    getCloudAncillaryDetailsHref,
    getCloudMovieDetailsHref,
    getCloudTvSeriesDetailsHref
} from "../../../assets/AssetsRoutes";
import { AssetOperationType } from "../../../assets/common/types/AssetOperationType";
import { CloudAssetStatus } from "../../../assets/details/cloud-asset/CloudAssetStatus";
import { ContentReferenceType } from "../../../common/types/ContentReferenceType";
import { AssetOperationList, AssetOperationListItemWithHref } from "./AssetOperationList";
import { AssetOperationListItem, ContentDetails } from "./AssetOperationListItem";
import { useCloudAssetOperations } from "./hooks/useCloudAssetOperations";

interface CloudAssetOperationListProps {
    cloudFileId?: string;
    setAssetContent?: (content: ContentDetails) => void;
}

export const CloudAssetOperationList = ({
    cloudFileId,
    setAssetContent
}: CloudAssetOperationListProps): JSX.Element | null => {
    const { data: cloudAssetOperations, isLoading } = useCloudAssetOperations(cloudFileId);

    useEffect(() => {
        if (setAssetContent && cloudAssetOperations && cloudAssetOperations.length > 0) {
            setAssetContent(cloudAssetOperations[0].content);
        }
    }, [cloudAssetOperations, setAssetContent]);

    if (!cloudFileId) {
        return null;
    }

    const operationsWithHref: AssetOperationListItemWithHref[] = (cloudAssetOperations ?? []).map((item) => ({
        ...item,
        resultStatus: mapResultStatus(item.operationType),
        href: getCloudAssetDetailsHref(item)
    }));

    if (!isLoading && !operationsWithHref.length) {
        return null;
    }

    return (
        <Stack>
            <Text variant="medium">
                <b>Cloud assets</b>
            </Text>
            <AssetOperationList operations={operationsWithHref} isLoading={isLoading} />
        </Stack>
    );
};

const mapResultStatus = (operationType: number) => {
    switch (operationType) {
        case AssetOperationType.New:
        case AssetOperationType.Retained:
            return CloudAssetStatus[CloudAssetStatus.Active];

        case AssetOperationType.Ignored:
        case AssetOperationType.Overridden:
            return CloudAssetStatus[CloudAssetStatus.Deprecated];

        default:
            throw Error(`Unsupported resulting operation type: ${operationType}`);
    }
};

const getCloudAssetDetailsHref = (item: AssetOperationListItem): string | undefined => {
    if (item.content.contentType === ContentReferenceType.Movie) {
        return getCloudMovieDetailsHref(item.id, item.content.subtype);
    }
    if (item.content.contentType === ContentReferenceType.TvSeries) {
        return getCloudTvSeriesDetailsHref(item.id);
    }
    if (item.content.contentType === ContentReferenceType.Ancillary) {
        return getCloudAncillaryDetailsHref(item.id, item.content.subtype);
    }

    return undefined;
};
